/**
 * CalendarStateContext - Data/State Only
 * Manages calendar data state (view, date, filters, users, settings)
 * Separated from actions to prevent unnecessary rerenders
 *
 * Settings use API format directly:
 * {
 *   show_weekends: boolean,
 *   show_birthdays: boolean,
 *   show_sol: boolean,
 *   show_business_times_only: boolean,
 *   sync_with_google_calendar: boolean,
 *   sync_with_outlook: boolean,
 *   calendar_id: string | null,
 *   default_calendar_view: string,
 *   primary: boolean
 * }
 */

import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { DEFAULT_VIEW, VIEW_NAME_MAP } from '../constants/calendarViews';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { DEFAULT_FILTERS } from '../constants/filterOptions';
import { useCalendarSettings } from '../hooks/useCalendarSettings';
import useEncryptedURLState from 'hooks/useEncryptedURLState';

const CalendarStateContext = createContext(undefined);

/**
 * Default calendar settings using API format
 */
const DEFAULT_CALENDAR_SETTINGS = {
  show_weekends: true,
  show_birthdays: true,
  show_sol: true,
  show_business_times_only: false,
  sync_with_google_calendar: false,
  sync_with_outlook: false,
  calendar_id: null,
  default_calendar_view: DEFAULT_VIEW,
  primary: true,
};

/**
 * Hook to access calendar state
 * @returns {object} Calendar state and setters
 * @throws {Error} If used outside CalendarStateProvider
 */
export const useCalendarState = () => {
  const context = useContext(CalendarStateContext);
  if (!context) {
    throw new Error('useCalendarState must be used within CalendarStateProvider');
  }
  return context;
};

/**
 * Calendar State Provider
 * Provides data state to child components
 */
export const CalendarStateProvider = ({ children, ...props }) => {
  const userContext = useUserDetailsContext();
  const isGlobal = !(props?.isFromCase || props?.isFromLead);

  // Fetch settings from API - Single source of truth
  const { settings: apiSettings, isLoading: settingsLoading } = useCalendarSettings();

  // Get initial view from userContext calendar_settings or use default
  // This ensures the calendar opens in the user's preferred default view
  // Convert simplified view names (e.g., 'week') to FullCalendar format (e.g., 'timeGridWeek')
  const savedView = userContext?.firmDetails?.calendar_settings?.default_calendar_view;
  const initialView = savedView ? VIEW_NAME_MAP[savedView] || savedView : DEFAULT_VIEW;

  // View & Date State - Combined for better state management
  // Note: currentView is initialized with user's default_calendar_view setting
  const [viewDateState, setViewDateState] = useEncryptedURLState('state', {
    currentView: initialView,
    currentDate: new Date(),
    dateRange: null,
  });

  // Filter State, Filter returns global filters if isGlobal is true, otherwise returns case/lead filters
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    ...(isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender || {} : {}),
  }));
  const [selectedUsers, setSelectedUsers] = useState(
    isGlobal
      ? Array.isArray(userContext?.firmDetails?.user_preferences?.filters?.calender?.firm_user)
        ? userContext?.firmDetails?.user_preferences?.filters?.calender?.firm_user
        : [userContext?.userDetails?.firm_user_id]
      : []
  );
  const [googleEventsEnabled, setGoogleEventsEnabled] = useState(new Set());

  // Settings State - Initialize with userContext calendar_settings for immediate render
  // Use API format directly - no mapping needed
  const [settings, setSettings] = useState(() => ({
    ...DEFAULT_CALENDAR_SETTINGS,
    ...(userContext?.firmDetails?.calendar_settings || {}),
  }));

  // Update settings state when API settings are fetched
  useEffect(() => {
    if (apiSettings && !settingsLoading) {
      setSettings({
        ...DEFAULT_CALENDAR_SETTINGS,
        ...apiSettings,
      });
    }
  }, [apiSettings, settingsLoading]);

  // Update calendar view when default_calendar_view setting changes
  useEffect(() => {
    if (settings?.default_calendar_view) {
      const newView = VIEW_NAME_MAP[settings.default_calendar_view] || settings.default_calendar_view;
      // Only update if the view has actually changed
      if (newView !== viewDateState.currentView) {
        setViewDateState((prev) => ({
          ...prev,
          currentView: newView,
        }));
      }
    }
  }, [settings?.default_calendar_view]);

  // Memoize value - only state, no callbacks
  const value = useMemo(
    () => ({
      // View & Date state
      viewDateState,
      setViewDateState,

      // Filter state
      filters,
      setFilters,
      selectedUsers,
      setSelectedUsers,
      googleEventsEnabled,
      setGoogleEventsEnabled,

      // Settings state
      settings,
      setSettings,

      // Props
      ...props,
      isGlobal,
    }),
    [viewDateState, filters, selectedUsers, googleEventsEnabled, settings]
  );

  return <CalendarStateContext.Provider value={value}>{children}</CalendarStateContext.Provider>;
};

export default CalendarStateContext;
